import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useReveal } from '@/hooks/useReveal';

// Iconos inline (sin dependencias). Trazo ámbar coherente con la marca.
const icons: Record<string, ReactNode> = {
  map: (
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Zm0 0v14m6-12v14" />
  ),
  layers: <path d="m12 3 9 5-9 5-9-5 9-5Zm9 9-9 5-9-5m18 4-9 5-9-5" />,
  users: (
    <path d="M16 19v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  ),
  tag: <path d="M3 11V5a2 2 0 0 1 2-2h6l9 9-8 8-9-9Zm5-4h.01" />,
};

function FeatureCard({
  icon,
  title,
  text,
  delay,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  delay: number;
}) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} group rounded-2xl border border-clay-300/10 bg-clay-900/40 p-7 transition hover:-translate-y-1 hover:border-amber-glow/40 hover:bg-clay-900/70`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-glow/10 text-amber-glow transition group-hover:bg-amber-glow group-hover:text-clay-950">
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-clay-50">{title}</h3>
      <p className="mt-2.5 leading-relaxed text-clay-300">{text}</p>
    </div>
  );
}

export function Features() {
  const { t } = useTranslation();
  const { ref, visible } = useReveal<HTMLDivElement>();

  const cards = [
    { icon: icons.map, title: t('features.interactive_title'), text: t('features.interactive_text') },
    { icon: icons.layers, title: t('features.layers_title'), text: t('features.layers_text') },
    { icon: icons.users, title: t('features.collaborative_title'), text: t('features.collaborative_text') },
    { icon: icons.tag, title: t('features.categories_title'), text: t('features.categories_text') },
  ];

  return (
    <section id="features" className="relative mx-auto max-w-6xl px-5 py-24 sm:py-32">
      <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''} mx-auto max-w-2xl text-center`}>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-clay-50 sm:text-4xl">
          {t('features.title')}
        </h2>
        <p className="mt-4 text-lg text-clay-300">{t('features.subtitle')}</p>
      </div>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <FeatureCard key={i} icon={c.icon} title={c.title} text={c.text} delay={i * 90} />
        ))}
      </div>
    </section>
  );
}
